import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  AdminProgrammeItem,
  AvailableUser,
  CohortAssessor,
  EligibleAssessor,
  EnrollmentWithUser,
  Page,
  ProductCode,
  Programme,
  ProgrammeLockReadiness,
  ProgrammeWithAssignments,
} from "@/types";

/**
 * React Query hooks for the admin Programmes feature (programmes list + CRUD and
 * per-programme enrollments). These wrap the existing API client methods — the
 * client is unchanged — and own the query keys, caching, and
 * invalidation-on-success that previously lived as manual `useEffect` loaders +
 * manual refetch in the pages. Follows the Sources slice template.
 *
 * List and enrollment params are baked into the query key, so changing
 * page/search/sort changes the key and React Query refetches naturally
 * (replacing the manual "reload on change" effects).
 */

export type ProgrammeView = "active" | "completed" | "archived" | "all";

export interface ProgrammesListParams {
  page: number;
  page_size: number;
  q?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  view?: ProgrammeView;
}

export type EnrollmentSort = "username" | "role" | "enrolled_at" | "status" | "current_week";

export interface EnrollmentsListParams {
  page: number;
  page_size: number;
  sort_by: EnrollmentSort;
  sort_order: "asc" | "desc";
  q?: string;
}

export type { AdminProgrammeItem };

export const programmeKeys = {
  all: ["admin", "programmes"] as const,
  lists: () => [...programmeKeys.all, "list"] as const,
  list: (params: ProgrammesListParams) => [...programmeKeys.lists(), params] as const,
  /** Nested under `all` so cohort writes invalidate the switcher list too. */
  switcher: () => [...programmeKeys.all, "switcher"] as const,
  detail: (id: string) => [...programmeKeys.all, "detail", id] as const,
  enrollments: (programmeId: string, params: EnrollmentsListParams) =>
    [...programmeKeys.all, "enrollments", programmeId, params] as const,
  availableUsers: (programmeId: string) =>
    [...programmeKeys.all, "availableUsers", programmeId] as const,
  assessors: (programmeId: string) => [...programmeKeys.all, "assessors", programmeId] as const,
  eligibleAssessors: (programmeId: string) =>
    [...programmeKeys.all, "eligibleAssessors", programmeId] as const,
  lockReadiness: (programmeId: string) =>
    [...programmeKeys.all, "lockReadiness", programmeId] as const,
};

export function useProgrammes(params: ProgrammesListParams) {
  return useQuery<Page<Programme>>({
    queryKey: programmeKeys.list(params),
    queryFn: () => api.getProgrammes(params),
  });
}

/**
 * The cohort list behind every admin cohort select (header switcher, ad-hoc
 * email tab, dashboard). One hook, one query key, deliberately (KC-387): this
 * was previously two identically-named hooks in `reviews` and `dashboard`, plus
 * a third key in a dead assignments hook, all hitting this endpoint under
 * *different* keys — so two surfaces could hold divergent cohort lists and
 * invalidating one never refreshed the other.
 *
 * Admin-only endpoint — pass `{ enabled: false }` for roles (e.g. assessors)
 * that may not read it, so the caller skips the request instead of 403-ing.
 */
export function useAdminProgrammes(options?: { enabled?: boolean }) {
  return useQuery<AdminProgrammeItem[]>({
    queryKey: programmeKeys.switcher(),
    queryFn: () => api.getAdminProgrammes(),
    enabled: options?.enabled ?? true,
  });
}

export function useProgramme(programmeId: string | undefined) {
  return useQuery<ProgrammeWithAssignments>({
    queryKey: programmeKeys.detail(programmeId ?? ""),
    queryFn: () => api.getProgramme(programmeId as string),
    enabled: Boolean(programmeId),
  });
}

export function useProgrammeEnrollments(
  programmeId: string | undefined,
  params: EnrollmentsListParams
) {
  return useQuery<Page<EnrollmentWithUser>>({
    queryKey: programmeKeys.enrollments(programmeId ?? "", params),
    queryFn: () => api.getProgrammeEnrollments(programmeId as string, params),
    enabled: Boolean(programmeId),
  });
}

export function useAvailableUsersForProgramme(programmeId: string | undefined) {
  return useQuery<AvailableUser[]>({
    queryKey: programmeKeys.availableUsers(programmeId ?? ""),
    queryFn: () => api.getAvailableUsersForProgramme(programmeId as string),
    enabled: Boolean(programmeId),
  });
}

/** Invalidate every Programmes query (lists, details, enrollments) after a write. */
function useInvalidateProgrammes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: programmeKeys.all });
}

export function useCreateProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: (data: {
      name: string;
      start_date: string;
      assignment_duration_days?: number;
      assignment_count?: number;
      open_time?: string;
      is_draft?: boolean;
      is_private?: boolean;
      price?: number;
      seats_limit?: number | null;
      max_submissions?: number;
      is_freemium?: boolean;
      product?: ProductCode;
    }) => api.createProgramme(data),
    onSuccess: invalidate,
  });
}

export function useUpdateProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Programme> }) =>
      api.updateProgramme(id, data),
    onSuccess: invalidate,
  });
}

export function useDuplicateProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: (id: string) => api.duplicateProgramme(id),
    onSuccess: invalidate,
  });
}

export function useDeleteProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: (id: string) => api.deleteProgramme(id),
    onSuccess: invalidate,
  });
}

/** Lock-readiness checklist for a cohort — drives the Lock dialog (KC-287). */
export function useProgrammeLockReadiness(programmeId: string | undefined, enabled = true) {
  return useQuery<ProgrammeLockReadiness>({
    queryKey: programmeKeys.lockReadiness(programmeId ?? ""),
    queryFn: () => api.getProgrammeLockReadiness(programmeId as string),
    enabled: Boolean(programmeId) && enabled,
  });
}

export function useLockProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: (id: string) => api.lockProgramme(id),
    onSuccess: invalidate,
  });
}

export function useUnlockProgramme() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: (id: string) => api.unlockProgramme(id),
    onSuccess: invalidate,
  });
}

export function useEnrollUser() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: ({ programmeId, userId }: { programmeId: string; userId: string }) =>
      api.enrollUserInProgramme(programmeId, userId),
    onSuccess: invalidate,
  });
}

export function useRemoveUser() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: ({ programmeId, enrollmentId }: { programmeId: string; enrollmentId: string }) =>
      api.removeUserFromProgramme(programmeId, enrollmentId),
    onSuccess: invalidate,
  });
}

export function useUpdateEnrollmentStatus() {
  const invalidate = useInvalidateProgrammes();
  return useMutation({
    mutationFn: ({
      programmeId,
      enrollmentId,
      status,
    }: {
      programmeId: string;
      enrollmentId: string;
      status: "active" | "completed" | "dropped";
    }) => api.updateEnrollmentStatus(programmeId, enrollmentId, status),
    onSuccess: invalidate,
  });
}

export function useCohortAssessors(programmeId: string | undefined) {
  return useQuery<CohortAssessor[]>({
    queryKey: programmeKeys.assessors(programmeId ?? ""),
    queryFn: () => api.getCohortAssessors(programmeId as string),
    enabled: Boolean(programmeId),
  });
}

export function useEligibleAssessors(programmeId: string | undefined) {
  return useQuery<EligibleAssessor[]>({
    queryKey: programmeKeys.eligibleAssessors(programmeId ?? ""),
    queryFn: () => api.getEligibleAssessors(programmeId as string),
    enabled: Boolean(programmeId),
  });
}

export function useAddCohortAssessor(programmeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.addCohortAssessor(programmeId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.assessors(programmeId) });
      queryClient.invalidateQueries({ queryKey: programmeKeys.eligibleAssessors(programmeId) });
    },
  });
}

export function useRemoveCohortAssessor(programmeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.removeCohortAssessor(programmeId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programmeKeys.assessors(programmeId) });
      queryClient.invalidateQueries({ queryKey: programmeKeys.eligibleAssessors(programmeId) });
    },
  });
}
